import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../services/api';

const EMPTY = { name:'', email:'', password:'', role:'staff', role_id:'' };

export default function UsersPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [showPass, setShowPass] = useState(false);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const { data, isLoading } = useQuery({
    queryKey:['users-mgmt'],
    queryFn:()=>api.get('/users-mgmt').then(r=>r.data),
  });
  const { data:rolesData } = useQuery({
    queryKey:['roles'],
    queryFn:()=>api.get('/roles').then(r=>r.data),
  });

  const createMut = useMutation({
    mutationFn:()=>api.post('/users-mgmt',form),
    onSuccess:()=>{ qc.invalidateQueries(['users-mgmt']); setShowForm(false); setForm(EMPTY); toast.success('User created'); },
    onError:e=>toast.error(e.response?.data?.message||'Error'),
  });
  const updateMut = useMutation({
    mutationFn:()=>api.put('/users-mgmt/'+editing.id,form),
    onSuccess:()=>{ qc.invalidateQueries(['users-mgmt']); setEditing(null); toast.success('Updated'); },
  });
  const deleteMut = useMutation({
    mutationFn:id=>api.delete('/users-mgmt/'+id),
    onSuccess:()=>{ qc.invalidateQueries(['users-mgmt']); toast.success('User deactivated'); },
  });

  const openEdit = u => {
    setEditing(u);
    setForm({name:u.name,email:u.email,password:'',role:u.role,role_id:u.role_id||''});
    setShowForm(false);
  };

  const users = data?.data || [];
  const roles = rolesData?.data || [];

  return (
    <div>
      <div className="g3" style={{marginBottom:20}}>
        <div className="card-sm"><div className="stat-label">Total Users</div><div className="stat-val">{users.length}</div></div>
        <div className="card-sm"><div className="stat-label">Active</div><div className="stat-val" style={{color:'var(--green)'}}>{users.filter(u=>u.is_active).length}</div></div>
        <div className="card-sm"><div className="stat-label">Admins</div><div className="stat-val" style={{color:'var(--info)'}}>{users.filter(u=>u.role==='admin').length}</div></div>
      </div>

      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16}}>
        <button className="btn btn-primary" onClick={()=>{setShowForm(v=>!v);setEditing(null);setForm(EMPTY);}}>+ Add User</button>
      </div>

      {(showForm||editing) && (
        <div className="card" style={{marginBottom:16}}>
          <div className="form-section-title">{editing?'Edit User':'New User'}</div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Full Name *</label>
              <input className="form-input" value={form.name} onChange={e=>set('name',e.target.value)} placeholder="Full name"/></div>
            <div className="form-group"><label className="form-label">Email *</label>
              <input className="form-input" type="email" value={form.email} onChange={e=>set('email',e.target.value)} placeholder="email@teknulugy.com"/></div>
            <div className="form-group"><label className="form-label">{editing?'New Password (leave blank to keep)':'Password *'}</label>
              <div style={{position:'relative'}}>
                <input className="form-input" type={showPass?'text':'password'} value={form.password} onChange={e=>set('password',e.target.value)} placeholder="••••••••" style={{paddingRight:60}}/>
                <button type="button" onClick={()=>setShowPass(v=>!v)} style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:11}}>
                  {showPass?'Hide':'Show'}
                </button>
              </div></div>
            <div className="form-group"><label className="form-label">System Role</label>
              <select className="form-select" value={form.role} onChange={e=>set('role',e.target.value)}>
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
                <option value="staff">Staff</option>
              </select></div>
            <div className="form-group"><label className="form-label">Custom Role (Permissions)</label>
              <select className="form-select" value={form.role_id} onChange={e=>set('role_id',e.target.value)}>
                <option value="">No custom role</option>
                {roles.map(r=><option key={r.id} value={r.id}>{r.name}</option>)}
              </select></div>
          </div>
          <div style={{display:'flex',gap:8,marginTop:8}}>
            <button className="btn btn-primary" onClick={()=>editing?updateMut.mutate():createMut.mutate()}>
              {editing?'Update User':'Create User'}
            </button>
            <button className="btn btn-outline" onClick={()=>{setShowForm(false);setEditing(null);}}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card">
        {isLoading?<div className="loading-center"><div className="spinner"/></div>:(
          <table className="tbl">
            <thead><tr><th>User</th><th>Email</th><th>System Role</th><th>Custom Role</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {users.length===0&&<tr><td colSpan={6} className="empty-state">No users found</td></tr>}
              {users.map(u=>(
                <tr key={u.id}>
                  <td><div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div className="avatar">{u.name.slice(0,2).toUpperCase()}</div>
                    <div style={{fontWeight:600}}>{u.name}</div>
                  </div></td>
                  <td style={{color:'var(--muted)'}}>{u.email}</td>
                  <td><span className={"badge "+(u.role==='admin'?'badge-purple':u.role==='manager'?'badge-info':'badge-muted')}>{u.role}</span></td>
                  <td>{u.roleData?<span className="badge badge-green">{u.roleData.name}</span>:<span style={{color:'var(--muted)',fontSize:12}}>--</span>}</td>
                  <td><span className={"badge "+(u.is_active?'badge-green':'badge-danger')}>{u.is_active?'Active':'Inactive'}</span></td>
                  <td><div style={{display:'flex',gap:6}}>
                    <button className="btn btn-outline btn-sm" onClick={()=>openEdit(u)}>Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={()=>{if(window.confirm('Deactivate?'))deleteMut.mutate(u.id);}}>X</button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
